'use client';

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, UserCheck, UserPlus, ShoppingCart, CreditCard, Award, ShieldAlert, AlertTriangle, Shield, Scale, XCircle, Mail, Info } from 'lucide-react';
import policyData from '../../parsed-policies.json';

const termsData = policyData.terms;
const sectionIcons = [FileText, UserCheck, UserPlus, ShoppingCart, CreditCard, Award, ShieldAlert, AlertTriangle, Shield, Scale, XCircle, Mail];

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFF]">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[35%] -right-[10%] w-[680px] h-[680px] bg-[#F9BC06]/20 rounded-full blur-[160px]" />
          <div className="absolute -bottom-[35%] -left-[5%] w-[520px] h-[520px] bg-[#60A5FA]/20 rounded-full blur-[140px]" />
          <div className="absolute inset-0 bg-[radial-gradient(900px_circle_at_20%_10%,rgba(255,155,22,0.18),transparent_55%),radial-gradient(700px_circle_at_90%_0%,rgba(96,165,250,0.2),transparent_45%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] bg-[size:28px_28px]" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-24 sm:pt-24 sm:pb-32 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center px-5 py-2 rounded-full bg-white/80 text-[#E5A905] text-[11px] font-semibold tracking-[0.28em] uppercase backdrop-blur-xl border border-orange-200/70 mb-6 shadow-[0_6px_20px_rgba(249,115,22,0.15)]">
              Homenest Retail Tech India Pvt Ltd
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#0B1220] mb-4 tracking-tight leading-[1.03]">
              Terms &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F9BC06] to-[#F9BC06]">
                Conditions
              </span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Please read these terms carefully before using our platform
            </p>
            <p className="text-sm text-slate-400 mt-4 font-medium">Last updated: February 6, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-12 relative z-10 pb-24">
        <div className="grid grid-cols-1 gap-6 sm:gap-7">
          {termsData.sections.map((section, index) => {
            const Icon = sectionIcons[index % sectionIcons.length];
            return (
              <motion.section
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="relative bg-white/80 backdrop-blur-2xl rounded-[24px] p-7 sm:p-10 border border-orange-100/80 shadow-[0_18px_50px_rgba(15,23,42,0.12)] hover:shadow-[0_28px_70px_rgba(15,23,42,0.18)] transition-all duration-500 overflow-hidden group"
              >
                <span className="absolute -top-4 right-4 sm:right-8 text-[110px] font-black text-orange-100/60 group-hover:text-orange-200/70 transition-colors duration-500 select-none pointer-events-none leading-none">
                  {String(index + 1).padStart(2, '0')}
                </span>

                <div className="relative">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-[#F9BC06] to-[#F9BC06] flex items-center justify-center shadow-[0_8px_20px_rgba(249,115,22,0.35)]">
                      <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-[#0B1220] tracking-tight">
                      {section.title}
                    </h2>
                  </div>

                  <div className="text-slate-600 space-y-4 pl-0 sm:pl-[60px]">
                    {section.content.split('\n\n').map((para, i) => {
                      if (para.includes('\u2022')) {
                        return (
                          <ul key={i} className="space-y-3">
                            {para.split('\n').filter(line => line.trim()).map((item, j) => (
                              <li key={j} className="flex items-start gap-3 text-[15px] leading-[1.85]">
                                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#F9BC06] mt-[11px]" />
                                <span>{item.replace('\u2022', '').trim()}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      if (para.startsWith('Note:')) {
                        return (
                          <div key={i} className="flex gap-4 bg-amber-50 rounded-2xl p-5 border border-amber-100">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                                <Info className="w-4 h-4 text-amber-600" />
                              </div>
                            </div>
                            <p className="text-[14px] text-amber-900/70 leading-relaxed font-medium">{para}</p>
                          </div>
                        );
                      }
                      return (
                        <p key={i} className="text-[15px] leading-[1.85]">
                          {para}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </motion.section>
            );
          })}
        </div>
      </div>
    </div>
  );
}
